import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { BottomSheet, Button, Notice } from '@sauda/ui/kit';
import {
  agreementSections,
  agreementStrings,
  type AgreementData,
  type EstimateData,
} from '@sauda/templates';
import { ApiError, api } from '../../lib/api.js';
import { whatsappLink } from '../../lib/share-text.js';
import { useState } from 'react';

/**
 * The draft as it will read in the PDF, built by the same function that builds the PDF, so the
 * broker can check every clause before saving. It carries the draft line and the estimate label.
 */
export function DraftPreviewSheet({
  open,
  onClose,
  data,
  estimate,
}: {
  open: boolean;
  onClose: () => void;
  data: AgreementData;
  estimate: EstimateData | null;
}) {
  const { t } = useTranslation();
  const sections = estimate === null ? null : agreementSections(data, estimate);
  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      label={t('deal.previewTitle')}
      title={t('deal.previewTitle')}
      closeLabel={t('app.close')}
    >
      {sections === null ? (
        <Notice tone="info">{t('deal.previewNeedsFigures')}</Notice>
      ) : (
        <div className="form-stack" data-testid="draft-preview" lang={sections.lang}>
          <p className="k-t-label-sm k-muted">{t('deal.previewNote')}</p>
          <p className="k-t-title-sm">{sections.title}</p>
          <Notice tone="warning">{sections.disclaimer}</Notice>
          <ol className="draft-clauses">
            {sections.clauses.map((clause) => (
              <li key={clause} className="k-t-body">
                {clause}
              </li>
            ))}
          </ol>
          <p className="k-t-title-sm">{sections.estimateHeading}</p>
          <p className="k-t-label-sm k-muted">{sections.estimateNote}</p>
          {sections.estimateRows.map(([label, value]) => (
            <p key={label} className="k-t-body">
              {label}: {value}
            </p>
          ))}
          <Notice tone="warning">{sections.disclaimer}</Notice>
        </div>
      )}
    </BottomSheet>
  );
}

interface ReviewResult {
  sentAt: string;
  tenant: { link: string; lang: 'en' | 'mr' | 'hi' };
  owner: { link: string; lang: 'en' | 'mr' | 'hi' };
}

/**
 * "Send draft for review": makes the draft readable on each side's status link, then gives the
 * broker one WhatsApp button per side, to send from their own phone. Nothing can be signed there.
 */
export function ReviewSheet({
  open,
  onClose,
  dealId,
  agency,
  property,
  tenantPhone,
  ownerPhone,
  onSent,
}: {
  open: boolean;
  onClose: () => void;
  dealId: string;
  agency: string;
  property: string;
  tenantPhone: string | null;
  ownerPhone: string | null;
  onSent: () => void;
}) {
  const { t } = useTranslation();
  const [result, setResult] = useState<ReviewResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const send = useMutation({
    mutationFn: () =>
      api<ReviewResult>(`/deals/${dealId}/agreement/review`, { method: 'POST', body: {} }),
    onSuccess: (r) => {
      setResult(r);
      setError(null);
      onSent();
    },
    onError: (e) =>
      setError(
        e instanceof ApiError && e.status < 500 && e.message !== '' ? e.message : t('deal.generic'),
      ),
  });
  const text = (side: ReviewResult['tenant']) =>
    agreementStrings(side.lang).reviewMessage(agency, property, side.link);
  return (
    <BottomSheet
      open={open}
      onClose={() => {
        setResult(null);
        onClose();
      }}
      label={t('deal.reviewTitle')}
      title={t('deal.reviewTitle')}
      closeLabel={t('app.close')}
    >
      <div className="form-stack">
        <p className="k-t-body">{t('deal.reviewHelp')}</p>
        {error === null ? null : <Notice tone="error">{error}</Notice>}
        {result === null ? (
          <Button size="lg" block loading={send.isPending} onClick={() => send.mutate()}>
            {t('deal.reviewMake')}
          </Button>
        ) : (
          <>
            <Notice tone="success">{t('deal.reviewReady')}</Notice>
            <a
              className="k-btn k-btn--filled k-btn--lg k-btn--block k-layer k-t-label-lg"
              href={whatsappLink(text(result.owner), ownerPhone)}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('deal.reviewSendOwner')}
            </a>
            <a
              className="k-btn k-btn--filled k-btn--lg k-btn--block k-layer k-t-label-lg"
              href={whatsappLink(text(result.tenant), tenantPhone)}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('deal.reviewSendTenant')}
            </a>
          </>
        )}
      </div>
    </BottomSheet>
  );
}
